import React, { useState, useEffect, useMemo } from "react";
import IconButton from "../../../../iconButton";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import {
  useSaveProfessionalsServicesMutation,
  useSaveProfessionalOfficePhotoNewMutation,
} from "../../../../../store/professionalInfo";
import { useSaveAdminProfessionalOfficePhotoMutation } from "../../../../../store/admin/professionalUserInfo";
import { useGetInsuranceDetailsQuery } from "../../../../../store/insuranceDetails";
import {
  getUpdateProfilePercentage,
  profilePercentages,
  validateEmail,
} from "../../../../../utilities/utils";
import { useGetProfessionsWithModalitiesQuery } from "../../../../../store/professions";
import ProfessionalGeneralInformation from "./ProfessionalGeneralInformation";
import ProfessionalOrganization from "./ProfessionalOrganization";
import ProfessionalOfficeAddress from "./ProfessionalOfficeAddress";
import ProfessionalContactInformation from "./ProfessionalContactInformation";
import ProfessionalServicesProvided from "./ProfessionalServicesProvided";
import ProfessionalSpecialties from "./ProfessionalSpecialities";
import ProfessionalInsurance from "./ProfessionalInsurance";
import {
  useAssignProfessionalServicesMutation,
  useGetUserDetailsQuery,
  useSaveAdminUserInfoMutation,
  useSaveAdminUserInfoPhotoMutation,
  useAssignProfesionsMutation,
} from "../../../../../store/admin/professionalUserInfo";
import { useLocation } from "react-router-dom";
import Loader from "../../../../commons/Loader";
import SignOutModal from "../../../../modal/SIgnOutModal";
import { toast } from "react-toastify";
import ProfessionalVirtualLead from "../../../../dashboard/dashboardSettings/userProfileDetail/ProfessionalVirtualLead";

const keyFormat = {
  notification_email: "personal email",
  email: "email",
};

const ProfessionalUserProfileSettings = () => {
  const location = useLocation();
  const { professionalId } = location?.state ?? {};
  const { data: professionaInfo, refetch: professionalInfoDetail } =
    useGetUserDetailsQuery(professionalId);

  const [
    saveAdminProfessionalInfo,
    {
      isLoading: infoIsLoading,
      isSuccess: saveIsSuccess,
      isError: saveIsError,
      error: saveError,
      data: savedProfessionalData,
    },
  ] = useSaveAdminUserInfoMutation();

  const [
    addProfessions,
    { isLoading: addProfessionsLoader, isSuccess: addProfessionsIsSuccess },
  ] = useAssignProfesionsMutation();

  const [saveAdminProfessionalInfoPhoto] = useSaveAdminUserInfoPhotoMutation();

  const [updateUser, setUpdatedUser] = useState(professionaInfo?.data);
  const [updatedImages, setUpdatedImages] = useState([]);
  const { data: insuranceDetails } = useGetInsuranceDetailsQuery();
  const [updatedUserData, setUpdatedUserData] = useState({});
  const [updatedServicesData, setUpdatedServicesData] = useState([]);
  const [updatedModalitiesData, setUpdatedModalitiesData] = useState([]);
  const [updatedSpecialties, setUpdatedSpecialties] = useState();
  const [updatedInsuranceData, setUpdatedInsuranceData] = useState([]);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isChange, setIsChange] = useState(false);
  const { data: allProfessionsWithModalities } =
    useGetProfessionsWithModalitiesQuery();

  const ProfessionModalities = useMemo(() => {
    if (allProfessionsWithModalities && professionaInfo?.data) {
      const foundProfession = allProfessionsWithModalities.find(
        (profession) =>
          profession?.id == professionaInfo.data?.professions[0]?.id
      );
      const updatedModalities = foundProfession
        ? foundProfession.modalities
        : [];
      return updatedModalities;
    }
  }, [allProfessionsWithModalities, professionaInfo?.data]);

  const ProfessionSpecialties = useMemo(() => {
    if (allProfessionsWithModalities && professionaInfo?.data) {
      const allSpecialty = allProfessionsWithModalities.filter(
        (profession) =>
          profession.parent_id == professionaInfo.data?.professions[0]?.id
      );
      return allSpecialty ? allSpecialty : [];
    }
  }, [allProfessionsWithModalities, professionaInfo?.data]);

  // const [
  //   saveProfessionalOfficePhoto,
  //   { isLoading: photosLoader, isSuccess: photosSuccess },
  // ] = useSaveProfessionalOfficePhotoMutation();

  const [
    saveProfessionalOfficePhoto,
    { isLoading: photosLoader, isSuccess: photosSuccess },
  ] = useSaveAdminProfessionalOfficePhotoMutation();

  const [
    saveProfessionalServices,
    { isSuccess, isLoading: professionalLoader },
  ] = useAssignProfessionalServicesMutation();

  useEffect(() => {
    if (professionaInfo?.data?.officePhotos) {
      let officePhotos = professionaInfo.data?.officePhotos;
      setUpdatedImages([...officePhotos]);
    }
    if (professionaInfo?.data?.insuranceCarriers) {
      setUpdatedInsuranceData(professionaInfo?.data?.insuranceCarriers);
    }
  }, [professionaInfo]);

  const handleUserChange = (e) => {
    let tempUpdateUser = { ...updateUser };
    if (
      e.target.name === "cell_phone_number" ||
      e.target.name === "office_phone_number" ||
      e.target.name === "sms_phone"
    ) {
      let updatedPhone = e.target.value.replace(/[-()\s]/g, "");

      if (updatedPhone.length < 11) {
        tempUpdateUser = { ...updateUser, [e.target.name]: updatedPhone };
        setUpdatedUser(tempUpdateUser);
        const isCheck = checkIsChangeUser(tempUpdateUser);
        setIsChange(isCheck);
      }
    } else if (e.target.name === "postal_code") {
      if (e.target.value.length === 5) {
        setErrorMessage(null);
        tempUpdateUser = { ...updateUser, [e.target.name]: e.target.value };
        setUpdatedUser(tempUpdateUser);
        const isCheck = checkIsChangeUser(tempUpdateUser);
        setIsChange(isCheck);
      } else {
        const isCheck = checkIsChangeUser(tempUpdateUser);
        if (isCheck) {
          setIsChange(false);
        }
        setErrorMessage("Zip code must be 5 digits.");
        tempUpdateUser = { ...updateUser, [e.target.name]: e.target.value };
        setUpdatedUser(tempUpdateUser);
      }
    } else {
      tempUpdateUser = { ...updateUser, [e.target.name]: e.target.value };
      setUpdatedUser(tempUpdateUser);
      const isCheck = checkIsChangeUser(tempUpdateUser);
      setIsChange(isCheck);
    }
  };

  const handleIsChangeArray = (data, stateProperty) => {
    const hasChanges =
      updateUser[stateProperty].length !== data.length ||
      updateUser[stateProperty].some(
        (item) => !data.some((updatedItem) => item.id === updatedItem.id)
      );
    return hasChanges;
  };

  const handleIsChangeServices = (data) => {
    let professionsServices = updateUser.professions
      .map((profession) => profession.services)
      .flat();
    let isChangeServices = professionsServices.some(
      (service) => !data.some((dt) => dt.id === service.id)
    );
    const hasChanges =
      professionsServices.length != data.length || isChangeServices;
    return hasChanges;
  };
  // check service, modalities or insurance carrier at the same time
  const handleSelectChangeArray = (services, modalities, insuranceCarriers) => {
    const serviceChange = handleIsChangeServices(services);
    const modalitiesChange = handleIsChangeArray(modalities, "modalities");
    const insuranceCarriersChange = handleIsChangeArray(
      insuranceCarriers,
      "insuranceCarriers"
    );
    return serviceChange || modalitiesChange || insuranceCarriersChange;
  };

  const handleSelectServices = (services) => {
    setIsChange(
      handleSelectChangeArray(
        services,
        updatedModalitiesData,
        updatedInsuranceData
      )
    );
    setUpdatedServicesData(services);
  };

  const handleSelectModalities = (modalities) => {
    setIsChange(
      handleSelectChangeArray(
        updatedServicesData,
        modalities,
        updatedInsuranceData
      )
    );
    setIsChange(handleIsChangeArray(modalities, "modalities"));
    setUpdatedModalitiesData(modalities);
  };

  const handleSelectSpecialties = (specialties) => {
    setIsChange(handleIsChangeArray(specialties, "professions"));
    setUpdatedSpecialties(specialties);
  };

  const handleSelectInsurance = (insurance) => {
    if (insurance) {
      setIsChange(
        handleSelectChangeArray(
          updatedServicesData,
          updatedModalitiesData,
          insurance
        )
      );
      setIsChange(handleIsChangeArray(insurance, "insuranceCarriers"));
      setUpdatedInsuranceData(insurance);
    }
  };

  useEffect(() => {
    if (saveIsSuccess && !infoIsLoading) {
      professionalInfoDetail();
      setIsChange(false);
      setConfirmationModal(false);
      setUpdatedUserData({});
    }
    if (photosSuccess && !photosLoader) {
      professionalInfoDetail();
      setIsChange(false);
      setUpdatedUserData({});
      setUpdatedImages([]);
    }
    if (saveIsError) {
      let saveApiError =
        saveError?.data?.errors?.fields?.length > 0
          ? saveError?.data?.errors?.fields[0]
          : "Something wrong with API";
      setIsChange(false);
      setUpdatedUser(professionaInfo?.data);
      setUpdatedImages([]);
      setConfirmationModal(false);
      toast.error(saveApiError);
    }
  }, [saveIsSuccess, photosSuccess, saveIsError]);

  useEffect(() => {
    professionalInfoDetail();
  }, []);

  useEffect(() => {
    if (professionaInfo) {
      setUpdatedUser(professionaInfo?.data);
      setIsChange(false);
    }
  }, [professionaInfo]);

  const checkIsChangeUser = (userInfo) => {
    let flag = false;
    const keys1 = Object.keys(userInfo);
    const keys2 = Object.keys(professionaInfo?.data);
    if (keys1.length !== keys2.length) {
      flag = true;
    }
    const tempParams = {};
    for (const key of keys1) {
      if (userInfo[key] && userInfo[key] != professionaInfo.data[key]) {
        tempParams[key] = userInfo[key];
        flag = true;
      } else {
        if (updatedUserData[key]) {
          delete updatedUserData[key];
        }
      }
    }
    flag && setUpdatedUserData({ ...updatedUserData, ...tempParams });
    return flag;
  };

  const handleSubmit = () => {
    let percentage = 0;
    let oldPercentage = parseInt(
      professionaInfo?.data?.profile_percentage ?? 0
    );
    if (updatedUserData && updatedUserData !== {}) {
      updatedUserData["_method"] = "PUT";
      const insuranceIds =
        updatedInsuranceData &&
        updatedInsuranceData.map((insurance) => insurance.id);

      if (handleIsChangeArray(updatedInsuranceData, "insuranceCarriers")) {
        updatedUserData["insuranceCarriers"] = insuranceIds;
      }
      if (updatedSpecialties?.length) {
        if (handleIsChangeArray(updatedSpecialties, "professions")) {
          const params = {
            professionalId: updateUser?.id,
            professions: updatedSpecialties,
          };
          addProfessions(params);
        }
      }
      if (updatedModalitiesData.length) {
        if (handleIsChangeArray(updatedModalitiesData, "modalities")) {
          const modalitiesIds =
            updatedModalitiesData &&
            updatedModalitiesData.map((modality) => modality.id);
          updatedUserData["modalities"] = modalitiesIds;
        }
      }
      if (updatedUserData.photo) {
        if (
          oldPercentage < getUpdateProfilePercentage(professionaInfo, "photo")
        ) {
          percentage = percentage + profilePercentages["photo"];
        }
        const payload = {
          professionalId,
          photo: updatedUserData.photo,
        };
        saveAdminProfessionalInfoPhoto({ ...payload });
        delete updatedUserData.photo;
      }
      for (const key in updatedUserData) {
        if (oldPercentage < getUpdateProfilePercentage(professionaInfo, key)) {
          percentage = percentage + profilePercentages[key];
        }
        if (key.includes("email")) {
          if (!validateEmail(updatedUserData[key])) {
            toast.error(`Enter a valid ${keyFormat[key]} address.`);
            handleClose();
            return;
          }
        }
      }

      saveAdminProfessionalInfo({ professionalId, ...updatedUserData });
    }
    if (handleIsChangeServices(updatedServicesData)) {
      const serviceIds =
        updatedServicesData && updatedServicesData.map((service) => service.id);
      let percentage;
      if (updateUser.profile_percentage === null) {
        percentage = 0;
      } else {
        percentage = parseInt(updateUser.profile_percentage);
      }

      let tempSelectedServices = updateUser.professions.some(
        (profession) => profession.services.length > 0
      );
      if (!tempSelectedServices) {
        percentage = profilePercentages["services"] + percentage;
      }
      if (serviceIds && serviceIds.length > 0) {
        const params = {
          professionalId: professionalId,
          services: serviceIds,
        };
        saveProfessionalServices(params);
      }
    }
    if (handleIsChangeArray(updatedImages, "officePhotos")) {
      saveOfficePhotos();
      if (
        oldPercentage <
        getUpdateProfilePercentage(professionaInfo, "officePhotos")
      ) {
        percentage = percentage + profilePercentages["officePhotos"];
      }
    }
    if (oldPercentage !== percentage && percentage != 0) {
      saveAdminProfessionalInfo({
        professionalId,
        ...{
          profile_percentage: percentage + oldPercentage,
          _method: "PUT",
        },
      });
    }
  };

  const handleProfileChange = (file) => {
    const tempUpdateUser = { ...updateUser, photo: file };
    setIsChange(checkIsChangeUser(tempUpdateUser));
    setUpdatedUser(tempUpdateUser);
  };

  const saveOfficePhotos = () => {
    const formData = new FormData();
    for (let item of updatedImages) {
      if (item.name) {
        formData.append("photos[]", item);
      }
    }
    const formLength = formData.getAll("photos[]").length;
    if (formLength > 0) {
      const param = {
        office_photos: formData,
        user_id: professionaInfo.data.id,
      };
      saveProfessionalOfficePhoto(param);
    }
  };

  const handleOfficePhotos = (photos) => {
    setIsChange(handleIsChangeArray(photos, "officePhotos"));
    setUpdatedImages(photos);
  };

  const allowProfessions = {
    Counselor: true,
  };

  const handleResetInsurance = (value) => {
    if (value == "yes") {
      setUpdatedInsuranceData(professionaInfo?.data?.insuranceCarriers);
      if (!Object.keys(updatedUserData).length > 0) {
        setIsChange(false);
        setUpdatedUser(professionaInfo?.data);
      }
    } else {
      setUpdatedInsuranceData([]);
      if (updatedUserData != {}) {
        setIsChange(handleIsChangeArray([], "insuranceCarriers"));
      }
    }
  };

  const handleSave = () => {
    handleSubmit();
  };

  const handleClose = () => {
    setConfirmationModal(false);
  };

  return !updateUser ? (
    <Loader isLoading={true} />
  ) : (
    <>
      <div className="block">
        <ProfessionalGeneralInformation
          professionaInfo={updateUser}
          handleChange={handleUserChange}
          handleProfileChange={handleProfileChange}
        />
        <ProfessionalOrganization
          professionaInfo={updateUser}
          handleChange={handleUserChange}
          originalProfessionalInfo={professionaInfo?.data}
        />
        <ProfessionalOfficeAddress
          professionaInfo={updateUser}
          updatedImages={updatedImages}
          handleOfficePhotos={handleOfficePhotos}
          handleChange={handleUserChange}
          errorMessage={errorMessage}
        />
        <ProfessionalContactInformation
          professionaInfo={updateUser}
          handleChange={handleUserChange}
        />
        {updateUser?.professions[0].name !== "Mental Health Counselor" && (
          <ProfessionalServicesProvided
            professionalInfo={updateUser}
            handleSelectServices={handleSelectServices}
          />
        )}
        {((ProfessionModalities && ProfessionModalities.length > 0) ||
          (ProfessionSpecialties && ProfessionSpecialties.length > 0)) &&
          updateUser?.professions[0].name == "Mental Health Counselor" && (
            <ProfessionalSpecialties
              updateUser={updateUser}
              modalities={ProfessionModalities}
              specialties={ProfessionSpecialties}
              handleSelectSpecialties={handleSelectSpecialties}
              handleSelectModalities={handleSelectModalities}
            />
          )}
        <ProfessionalInsurance
          insuranceDetails={insuranceDetails ?? []}
          selectedInsuranceDetails={updateUser?.insuranceCarriers ?? []}
          handleSelectInsurance={handleSelectInsurance}
          handleResetInsurance={handleResetInsurance}
        />
        {isChange && (
          <div className="flex items-center gap-5 justify-end md:justify-end mt-[30px] fixed bottom-0 right-0 bg-white py-4 md:py-[30px] px-5 md:px-[35px] w-full">
            <IconButton
              title="Save"
              onClick={() => setConfirmationModal(true)}
              icon={!isChange && CheckCircleIcon}
              iconClasses="w-6 h-6 text-white"
              classes="text-sm font-black leading-[160%] text-white max-w-[160px] md:max-w-[128px] w-full flex items-center justify-center gap-[6px] bg-primary h-10 rounded-[10px]"
            />
          </div>
        )}
      </div>
      <SignOutModal
        isOpen={confirmationModal}
        handleClose={handleClose}
        handleSignOut={handleSave}
        text="Are you sure you want to save these changes? This cannot be undone."
        color="#16CA7C"
        loader={infoIsLoading}
      />
    </>
  );
};

export default ProfessionalUserProfileSettings;
